import {
  PROMPT_PAGES_PATH,
  PROMPT_SHORT_SHARE_KIND,
  buildPromptPageTargetPath,
} from '../../api/_lib/shortShareRules';

export const PUBLIC_SITE_ORIGIN = 'https://journalparty.com';

const INTERNAL_SHARE_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const INTERNAL_SHARE_PROTOCOLS = new Set(['capacitor:', 'ionic:']);

export interface PublicShareTrackingParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

const DEFAULT_SHARE_MEDIUM_BY_PATH_PREFIX: Array<[string, string]> = [
  ['/p/', 'program_share'],
  ['/e/', 'event_share'],
  ['/j/', 'journey_share'],
  ['/journal-prompts/', 'prompt_share'],
];

function hasInternalShareHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return INTERNAL_SHARE_HOSTS.has(normalized) || normalized.endsWith('.local');
}

export function resolvePublicShareOrigin(origin?: string | null): string {
  const candidate = String(origin ?? '').trim();
  if (!candidate) return PUBLIC_SITE_ORIGIN;

  try {
    const url = new URL(candidate);
    if (INTERNAL_SHARE_PROTOCOLS.has(url.protocol) || hasInternalShareHost(url.hostname)) {
      return PUBLIC_SITE_ORIGIN;
    }

    // Preview hosts and git aliases (*.vercel.app) are not the public site.
    if (url.hostname.toLowerCase().endsWith('.vercel.app')) {
      return PUBLIC_SITE_ORIGIN;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return PUBLIC_SITE_ORIGIN;
    }

    return url.origin.replace(/\/+$/, '');
  } catch {
    return PUBLIC_SITE_ORIGIN;
  }
}

export function getPublicShareOrigin(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return resolvePublicShareOrigin(origin);
}

export function resolveExternalWebOrigin(origin?: string | null): string {
  const candidate = String(origin ?? '').trim();
  if (!candidate) return PUBLIC_SITE_ORIGIN;

  try {
    const url = new URL(candidate);
    if (INTERNAL_SHARE_PROTOCOLS.has(url.protocol)) {
      return PUBLIC_SITE_ORIGIN;
    }

    const hostname = url.hostname.toLowerCase();
    if (hasInternalShareHost(hostname) && !url.port) {
      return PUBLIC_SITE_ORIGIN;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return PUBLIC_SITE_ORIGIN;
    }

    return url.origin.replace(/\/+$/, '');
  } catch {
    return PUBLIC_SITE_ORIGIN;
  }
}

export function getExternalWebOrigin(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return resolveExternalWebOrigin(origin);
}

export function buildExternalWebUrl(
  path: string,
  params?: Record<string, string | number | boolean | null | undefined>,
  origin?: string | null
): string {
  const base = resolveExternalWebOrigin(origin ?? getExternalWebOrigin());
  const url = new URL(path.startsWith('/') ? path : `/${path}`, base);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    url.searchParams.set(key, String(value));
  });

  return url.toString();
}

export function buildPublicShareUrl(
  path: string,
  trackingParams?: PublicShareTrackingParams,
  origin?: string | null
): string {
  const safePath = path.startsWith('/') ? path : `/${path}`;
  const base = resolvePublicShareOrigin(origin ?? getPublicShareOrigin());
  const url = new URL(safePath, base);

  if (!hasPublicShareTrackingParams(trackingParams)) {
    return url.toString();
  }

  const shortSharePath = getShortSharePath(url.pathname);
  if (shortSharePath) {
    const shortUrl = new URL(shortSharePath, base);
    const defaultMedium = getDefaultShareMedium(url.pathname);

    if (trackingParams?.utm_source) {
      shortUrl.searchParams.set('s', trackingParams.utm_source);
    }
    if (trackingParams?.utm_medium && trackingParams.utm_medium !== defaultMedium) {
      shortUrl.searchParams.set('m', trackingParams.utm_medium);
    }
    if (trackingParams?.utm_campaign && trackingParams.utm_campaign !== 'organic') {
      shortUrl.searchParams.set('c', trackingParams.utm_campaign);
    }

    return shortUrl.toString();
  }

  appendPublicShareTrackingParams(url, trackingParams);

  const fallbackShortUrl = new URL('/r/share', base);
  fallbackShortUrl.searchParams.set('next', `${url.pathname}${url.search}${url.hash}`);
  return fallbackShortUrl.toString();
}

function hasPublicShareTrackingParams(params?: PublicShareTrackingParams): boolean {
  return Boolean(params?.utm_source || params?.utm_medium || params?.utm_campaign);
}

function appendPublicShareTrackingParams(
  url: URL,
  trackingParams?: PublicShareTrackingParams
): void {
  if (trackingParams?.utm_source) {
    url.searchParams.set('utm_source', trackingParams.utm_source);
  }
  if (trackingParams?.utm_medium) {
    url.searchParams.set('utm_medium', trackingParams.utm_medium);
  }
  if (trackingParams?.utm_campaign) {
    url.searchParams.set('utm_campaign', trackingParams.utm_campaign);
  }
}

function getDefaultShareMedium(pathname: string): string | null {
  return DEFAULT_SHARE_MEDIUM_BY_PATH_PREFIX.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? null;
}

function getShortSharePath(pathname: string): string | null {
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length !== 2) {
    return null;
  }

  const [kind, slugOrId] = pathSegments;
  if (!slugOrId) {
    return null;
  }

  if (kind === 'p' || kind === 'e' || kind === 'j') {
    return `/r/${kind}/${encodeURIComponent(decodePathSegment(slugOrId))}`;
  }

  // Prompt pages get /r/jp/<page> short links only for a plain slug, the same
  // rule (lowercased) that api/ssr.ts and acquisitionAttribution.ts enforce.
  if (`/${kind}` === PROMPT_PAGES_PATH && buildPromptPageTargetPath(slugOrId)) {
    return `/r/${PROMPT_SHORT_SHARE_KIND}/${slugOrId.toLowerCase()}`;
  }

  return null;
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
